import { ReactElement, useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';

import api from 'config/api';
import AlertDanger from 'components/AlertDanger';
import AlertSuccess from 'components/AlertSuccess';
import InputOtp from 'components/InputOtp';
import { Indicator } from 'types/indicator';

import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

// This schema is used to validate the enter form.
const schema = yup.object().shape({
  name: yup.string().required('Please provide your name'),
  email: yup
    .string()
    .email("Ups! it's not a valid email address")
    .required('Please provide your email address'),
  password: yup.string().required('Please fill your password'),
  txd_key: yup.string().required('Please fill the TXD-Key'),
});

type Requirement = {
  name: string;
  email: string;
  password: string;
  txd_key: string;
};

function PreEnter(props: any): ReactElement<HTMLDivElement> {
  const [status, setStatus] = useState<Indicator>('idle');

  const {
    formState: { errors },
    getValues,
    handleSubmit,
    register,
    setValue,
    setError,
  } = useForm<Requirement>({
    resolver: yupResolver(schema),
  });

  // Call this callback when the user submit the form. The callback will send
  // the credentials via API.
  const onSubmit: SubmitHandler<Requirement> = async (form) => {
    setStatus('loading');

    try {
      const requestConfig = {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(form),
      };

      let response = await fetch(api.pre_enter, requestConfig);

      if (response.status === 422) {
        let { errors: validationErrors } = await response.json();

        setStatus('error');

        for (let [field, error] of Object.entries(validationErrors)) {
          setError(field as any, {
            type: 'manual',
            message: (error as string[]).join(' '),
          });
        }
      } else if (response.status === 201) {
        setStatus('ok');
      }
    } catch {
      // Error reporting here
      //
      // TODO: add failure service like sanity
    }
  };

  const toNextOtp = (e: any) => {
    if (e.currentTarget.value !== '') {
      e.currentTarget.nextSibling && e.currentTarget.nextSibling.focus();
      setValue('txd_key', getValues('txd_key') + e.currentTarget.value);
    } else {
      e.currentTarget.previousSibling &&
        e.currentTarget.previousSibling.focus();
      setValue(
        'txd_key',
        getValues('txd_key').slice(0, getValues('txd_key').length - 1)
      );
    }
  };

  const moveToLeftOrRightOtp = (e: any) => {
    if (e.key === 'ArrowLeft') {
      e.currentTarget.previousSibling &&
        e.currentTarget.previousSibling.focus();
    } else if (e.key === 'ArrowRight') {
      e.currentTarget.nextSibling && e.currentTarget.nextSibling.focus();
    }
  };

  return (
    <div className="mx-auto py-20 max-w-3xl">
      <h1 className="mb-28 text-3xl font-display">
        Yo! Welcome, <span className="text-primary">candidate.</span>
      </h1>
      <div className="h-full grid grid-cols-2 gap-x-10">
        <form onSubmit={handleSubmit(onSubmit)}>
          {status === 'error' && (
            <AlertDanger>
              Ups! there's something missing. Please check your data
            </AlertDanger>
          )}
          {status === 'ok' && <AlertSuccess>Pre-enter success!</AlertSuccess>}
          <div className="mb-5 last:mb-0">
            <label
              className="mb-1 inline-block text-secondary text-sm font-display"
              htmlFor="name"
            >
              Name
            </label>
            <input
              className="py-3 px-5 w-full bg-gray-200 rounded-lg text-sm focus:outline-none"
              type="text"
              id="name"
              {...register('name')}
            />
            {errors.name?.message && (
              <span className="text-xs text-primary font-body">
                {errors.name.message}
              </span>
            )}
          </div>
          <div className="mb-5 last:mb-0">
            <label
              className="mb-1 inline-block text-secondary text-sm font-display"
              htmlFor="email"
            >
              Email
            </label>
            <input
              className="py-3 px-5 w-full bg-gray-200 rounded-lg text-sm focus:outline-none"
              type="email"
              id="email"
              {...register('email')}
            />
            {errors.email?.message && (
              <span className="text-xs text-primary font-body">
                {errors.email.message}
              </span>
            )}
          </div>
          <div className="mb-5 last:mb-0">
            <label
              className="mb-1 inline-block text-secondary text-sm font-display"
              htmlFor="password"
            >
              Password
            </label>
            <input
              className="py-3 px-5 w-full bg-gray-200 rounded-lg text-sm focus:outline-none"
              type="password"
              id="password"
              {...register('password')}
            />
            {errors.password?.message && (
              <span className="text-xs text-primary font-body">
                {errors.password.message}
              </span>
            )}
          </div>
          <div className="mb-5 last:mb-0">
            <label
              className="mb-1 inline-block text-secondary text-sm font-display"
              htmlFor="txd_key"
            >
              TXD-Key
            </label>
            <div className="grid grid-cols-6 gap-x-5">
              <input type="hidden" {...register('txd_key')} />
              {Array(6)
                .fill(1)
                .map((value, idx) => (
                  <InputOtp
                    key={idx}
                    role={`input-otp-${++idx}`}
                    onInput={toNextOtp}
                    onKeyDown={moveToLeftOrRightOtp}
                  />
                ))}
            </div>
            {errors.txd_key?.message && (
              <span className="text-xs text-primary font-body">
                {errors.txd_key.message}
              </span>
            )}
          </div>
          <div>
            <button className="py-3 w-full bg-primary flex flex-row justify-center text-white font-display hover:bg-secondary">
              {status === 'loading' ? 'LOADING...' : 'PRE-ENTER'}
            </button>
          </div>
        </form>
        <div>
          <img
            src="https://res.cloudinary.com/trycatch/image/upload/v1627584602/thexdev/common/ui_illustration_nope_api_wjodpf.svg"
            alt="nope api illustration"
          />
        </div>
      </div>
    </div>
  );
}

export default PreEnter;
